import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import type { ContentStatus, StudioContentStatus } from "@/lib/schema";
import {
  Archive,
  CheckCircle,
  MoreHorizontal,
  Pencil,
  Rocket,
  RotateCcw,
  Send,
  Undo2,
  XCircle,
} from "lucide-react";

interface LifecycleActionsMenuProps {
  itemId: string;
  status: StudioContentStatus | ContentStatus;
  createdBy?: string | null;
  authorCoachId?: string | null;
  onEdit?: (id: string) => void;
  onSubmit?: (id: string) => void;
  onWithdraw?: (id: string) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onPublish?: (id: string) => void;
  onArchive?: (id: string) => void;
  onRestore?: (id: string) => void;
  stopPropagation?: boolean;
}

export function LifecycleActionsMenu({
  itemId,
  status,
  createdBy,
  authorCoachId,
  onEdit,
  onSubmit,
  onWithdraw,
  onApprove,
  onReject,
  onPublish,
  onArchive,
  onRestore,
  stopPropagation = true,
}: LifecycleActionsMenuProps) {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isCoach = roles.includes("coach");
  const isAdmin = roles.includes("admin") || roles.includes("super_admin");
  const isOwner =
    !!user &&
    ((!!createdBy && createdBy === user.id) ||
      (!!authorCoachId && authorCoachId === user.id));

  const wrap = (handler?: (id: string) => void) => (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    handler?.(itemId);
  };

  const isDraft = status === "draft";
  const isRejected = status === "changes_requested";
  const isSubmitted = status === "submitted";
  const isApproved = (status as string) === "approved";
  const isLive = status === "published";
  const isArchived = status === "archived";

  const canEdit =
    status !== "outdated" &&
    ((isCoach && isOwner && (isDraft || isRejected)) ||
      (isAdmin && !isLive && !isArchived));
  const canSubmit =
    (isDraft || isRejected) && (isAdmin || (isCoach && isOwner));
  const canWithdraw = isSubmitted && (isAdmin || (isCoach && isOwner));
  const canApprove = isSubmitted && isAdmin && !isOwner;
  const canReject = isSubmitted && isAdmin && !isOwner;
  const canPublish = isAdmin && isApproved && !isOwner;
  const canArchive = isAdmin && isLive;
  // Server allows restore for admins (any content) and coaches on their OWN
  // content (ownership enforced via assertOwner). Mirror that in the UI.
  const canRestore = isArchived && (isAdmin || (isCoach && isOwner));

  const hasActions =
    (canEdit && onEdit) ||
    (canSubmit && onSubmit) ||
    (canWithdraw && onWithdraw) ||
    (canApprove && onApprove) ||
    (canReject && onReject) ||
    (canPublish && onPublish) ||
    (canArchive && onArchive) ||
    (canRestore && onRestore);

  if (!hasActions) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
        }}
      >
        <Button
          variant="ghost"
          size="icon"
          data-testid={`button-actions-${itemId}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEdit && onEdit && (
          <DropdownMenuItem
            onClick={wrap(onEdit)}
            data-testid={`action-edit-${itemId}`}
          >
            <Pencil className="h-4 w-4 mr-2" />
            {isLive ? "View" : "Edit"}
          </DropdownMenuItem>
        )}
        {canSubmit && onSubmit && (
          <DropdownMenuItem
            onClick={wrap(onSubmit)}
            data-testid={`action-submit-${itemId}`}
          >
            <Send className="h-4 w-4 mr-2" />
            Submit for Approval
          </DropdownMenuItem>
        )}
        {canWithdraw && onWithdraw && (
          <DropdownMenuItem
            onClick={wrap(onWithdraw)}
            data-testid={`action-withdraw-${itemId}`}
          >
            <Undo2 className="h-4 w-4 mr-2" />
            Pull back to Draft
          </DropdownMenuItem>
        )}
        {canApprove && onApprove && (
          <DropdownMenuItem
            onClick={wrap(onApprove)}
            data-testid={`action-approve-${itemId}`}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve
          </DropdownMenuItem>
        )}
        {canReject && onReject && (
          <DropdownMenuItem
            onClick={wrap(onReject)}
            className="text-destructive"
            data-testid={`action-reject-${itemId}`}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Send back with notes
          </DropdownMenuItem>
        )}
        {canPublish && onPublish && (
          <DropdownMenuItem
            onClick={wrap(onPublish)}
            data-testid={`action-publish-${itemId}`}
          >
            <Rocket className="h-4 w-4 mr-2" />
            Publish
          </DropdownMenuItem>
        )}
        {canRestore && onRestore && (
          <DropdownMenuItem
            onClick={wrap(onRestore)}
            data-testid={`action-restore-${itemId}`}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Restore to Draft
          </DropdownMenuItem>
        )}
        {(canSubmit ||
          canApprove ||
          canReject ||
          canPublish ||
          canWithdraw ||
          canRestore) &&
          canArchive && <DropdownMenuSeparator />}
        {canArchive && onArchive && (
          <DropdownMenuItem
            onClick={wrap(onArchive)}
            className="text-destructive"
            data-testid={`action-archive-${itemId}`}
          >
            <Archive className="h-4 w-4 mr-2" />
            Archive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
